import React, { useState, useRef, useEffect } from "react";
import { PiCaretDown, PiCaretUp } from "react-icons/pi";
import "./BeneficiaryComboBox.css";

interface BeneficiaryComboBoxProps {
  value: string | null;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  id?: string;
}

const BeneficiaryComboBox: React.FC<BeneficiaryComboBoxProps> = ({
  value,
  onChange,
  options,
  placeholder = "Enter or select beneficiary",
  id = "beneficiary",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update input value when prop value changes
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(true); // Open dropdown when typing
  };

  const handleOptionClick = (option: string) => {
    setInputValue(option);
    onChange(option);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      inputRef.current?.focus();
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="beneficiary-combobox" ref={containerRef}>
      <div className="beneficiary-combobox-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="beneficiary-combobox-input"
        />
        <button
          type="button"
          className="beneficiary-combobox-toggle"
          onClick={toggleDropdown}
          tabIndex={-1}
        >
          {isOpen ? <PiCaretUp /> : <PiCaretDown />}
        </button>
      </div>
      {isOpen && options.length > 0 && (
        <div className="beneficiary-combobox-dropdown">
          {options.map((option, index) => (
            <div
              key={index}
              className={`beneficiary-combobox-option ${option === inputValue ? "selected" : ""
                }`}
              onClick={() => handleOptionClick(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BeneficiaryComboBox;
